//Framework Imports
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import ReactTooltip from 'react-tooltip';
import DatePicker from 'react-datepicker';
import SelectMulti, { MultiValue } from 'react-select';
import { useAppSelector } from '../../../app/hooks';

//Calendar Imports
import FullCalendar, { EventContentArg } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import ReactPhoneInput from 'react-phone-input-2';
import { EventSource } from '../../../types/eventSource';
import { calendarColorCodes } from '../../../common/constants';
import moment from 'moment';

//Component Imports
import { FieldInput } from '../../../components/fieldInput';
import Layout from '../../../components/layout';
import EditModal from '../../../components/editModal';
import SubmitButton from '../../../components/submitButton';
import { Notification } from '../../../components/notifications';
import { ResponseStatus } from '../../../components/notifications';

//Data Imports
import { getStates } from '../../../api/services/stateService';
import { getBillingSpecialists, getDivisions } from '../../../api/services/divisionService';
import { getDistrict, getDistrictBillingSpecialists, getTherapyTeam } from '../../../api/services/districtService';
import { getBillingRatesByDistrictId } from '../../../api/services/billingRateService';
import { createCalendarItem, deleteCalendarItem, getCalendarItemsByDistrictId } from '../../../api/services/calendarItemService';
import { getDisciplines } from '../../../api/services/disciplineService';
import { updateDistrict, deleteDistrict } from '../../../api/services/districtService';
import { createDirector, getDirectorsByDistrictId, deleteDirector, getDirectors, getDirectorById, updateDirector } from '../../../api/services/directorService';
import { createContact, updateContact, deleteContact } from '../../../api/services/contactService';

//Model Imports
import Director from '../../../api/models/director';
import Contact from '../../../api/models/contact';
import BillingRate from '../../../api/models/billingRate';
import Discipline from '../../../api/models/discipline';
import CalendarItem from '../../../api/models/calendarItem';
import DistrictBillingSpecialist from '../../../api/models/districtBillingSpecialist';
import TherapyTeamUser from '../../../api/models/therapyTeamUser';
import TherapyTeam from '../../../api/models/therapyTeam';
import DirectorDiscipline from '../../../api/models/directorDisciplines';
import Program from '../../../api/models/program';
import User from '../../../api/models/user';

//Request Imports
import DirectorRequest from '../../../api/requests/directorRequest';
import ContactRequest from '../../../api/requests/contactRequest';
import CalendarItemRequest from '../../../api/requests/calendarItemRequest';

// Misc Imports
import { selectOption } from '../../../types/selectOption';
import { formatPhoneNumber, hasRole, hasValue } from '../../../common/utils';
import { ConfirmPendingChanges } from '../../../common/constants';
import { CheckIcon, PencilIcon, UserCircleIcon } from '@heroicons/react/outline';
import { getDisciplineGroups } from '../../../api/services/disciplineGroupService';
import DisciplineGroup from '../../../api/models/disciplineGroup';
import { Role } from '../../../common/constants';


type ViewState = { message?: string; }; //Get our view state
type Params = { id: string; }; //GET params

//Async fetch functions 
const fetchDistrict = async ({ id }: any) => await getDistrict(id);
const fetchDisciplines = async () => await getDisciplines();
const fetchCalendarItemsByDistrictId = async ({ id }: any) => await getCalendarItemsByDistrictId(id);
const fetchBillingRatesByDistrictId = async ({ id }: any) => await getBillingRatesByDistrictId(id);
const fetchDirectorsByDistrictId = async ({ id }: any) => await getDirectorsByDistrictId(id);
const fetchTherapyTeam = async ({ id }: any) => await getTherapyTeam(id);
const fetchBillingSpecialists = async ({ id }: any) => await getBillingSpecialists();
const fetchDistrictBillingSpecialists = async ({ id }: any) => await getDistrictBillingSpecialists(id);

//Primary component
const EditDistrict = () => {
  //Get our parameter and state data
  const { state } = useLocation<ViewState>();
  const { id } = useParams<Params>();
  const history = useHistory();

  // Used to track if the user made changes to the page before exiting
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const changesMade = () => { setHasChanges(true); }

  //Control vars
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [isOperationsUser, setIsOperationsUser] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isBillingSpecialist, setIsBillingSpecialist] = useState<boolean>(false);
  const userRoles = useAppSelector((s) => s.user.roles);

  //Data
  const [districtId, setDistrictId] = useState<number | undefined>();
  const [divisionId, setDivisionId] = useState<number>(0);
  const [stateId, setStateId] = useState<number>(0);
  const [directorsData, setDirectorsData] = useState<Director[]>([]);
  const [allDirectors, setAllDirectors] = useState<User[]>([]);
  const [contactsData, setContactsData] = useState<Contact[]>([]);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [disciplineGroups, setDisciplineGroups] = useState<DisciplineGroup[]>([]);
  const [therapyTeam, setTherapyTeam] = useState<TherapyTeam>();
  const [programs, setPrograms] = useState<Program[]>();

  //calendar
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<EventSource[]>([]);

  //Open modal booleans
  const [openAddDirectorModal, setOpenAddDirectorModal] = useState(false);
  const [openRemoveDirectorModal, setOpenRemoveDirectorModal] = useState(false);
  const [openAddContactModal, setOpenAddContactModal] = useState(false);
  const [openAddCalendarModal, setOpenAddCalendarModal] = useState(false);
  const [openCalendarNotesModal, setOpenCalendarNotesModal] = useState(false);

  //District Fields
  const [name, setName] = useState('');
  const [kellyCustomerNumber, setKellyCustomerNumber] = useState<number>();
  const [visible, setVisible] = useState(false);
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [phone, setPhone] = useState('');
  const [allowsDistrictTime, setAllowsDistrictTime] = useState(false);
  const [requiresMonthlyCaseNotes, setRequiresMonthlyCaseNotes] = useState(false);
  const [quickbooksName, setQuickbooksName] = useState('');
  //const [targetHours, setTargetHours] = useState<number>(450 / 60);
  const [dailyAllowance, setDailyAllowance] = useState(0);
  const [dailyOverageAllowance, setDailyOverageAllowance] = useState(0);
  const [unlimitedOverageAllowed, setUnlimitedOverageAllowed] = useState(false);
  const [isOverageUnlimited, setIsOverageUnlimited] = useState(false);
  const [overageAllowed, setOverageAllowed] = useState(false)
  const [fixedTargetHours, setFixedTargetHours] = useState(true);
  const [contractReceivedDate, setContractReceivedDate] = useState<Date>();
  const [contractExpirationYear, setContractExpirationYear] = useState<Date>();
  const [isMultiYearContract, setIsMultiYearContract] = useState(false);

  //Director Fields
  const [directorId, setDirectorId] = useState<number | undefined>();
  const [addDirectorName, setAddDirectorName] = useState('');
  const [addDirectorUserId, setAddDirectorUserId] = useState<number>(0);
  const [addDirectorDiscipline, setAddDirectorDiscipline] = useState<DirectorDiscipline>();
  const [addDirectorDisciplines, setAddDirectorDisciplines] = useState<DirectorDiscipline[]>([]);
  const [addDirectorDisciplineGroup, setAddDirectorDisciplineGroup] = useState<DisciplineGroup>();
  const [addDirectorDisciplineGroups, setAddDirectorDisciplineGroups] = useState<DisciplineGroup[]>([]);

  //Contacts Fields
  const [addContactId, setAddContactId] = useState<number>(0);
  const [addContactName, setAddContactName] = useState('');
  const [addContactLastName, setAddContactLastName] = useState('');
  const [addContactIsInvoice, setAddContactIsInvoice] = useState(false);
  const [addContactTitle, setAddContactTitle] = useState('');
  const [addContactEmail, setAddContactEmail] = useState('');
  const [addContactPhone, setAddContactPhone] = useState('');
  const [addContactFax, setAddContactFax] = useState('');
  const [addContactAddress1, setAddContactAddress1] = useState('');
  const [addContactAddress2, setAddContactAddress2] = useState('');
  const [addContactCity, setAddContactCity] = useState('');
  const [addContactStateId, setAddContactStateId] = useState('');
  const [addContactZipcode, setAddContactZipcode] = useState('');
  const [addContactNotes, setAddContactNotes] = useState('');

  //Calendar Fields
  const [addCalendarItemId, setAddCalendarItemId] = useState<number>(0);
  const [addCalendarItemStartDate, setAddCalendarItemStartDate] = useState<Date>(new Date());
  const [addCalendarItemEndDate, setAddCalendarItemEndDate] = useState<Date>(new Date());
  const [addCalendarItemColorCode, setAddCalendarItemColorCode] = useState<number>(-1);
  const [addCalendarItemIsVisible, setAddCalendarItemIsVisible] = useState(true);
  const [addCalendarItemNotes, setAddCalendarItemNotes] = useState('');
  const [addCalendarItemTitle, setAddCalendarItemTitle] = useState('');
  const [calendarNotes, setCalendarNotes] = useState('');

  //Billing Rates Fields
  const [billingRatesPending, setBillingRatesPending] = useState<BillingRate[]>([]);
  const [billingRatesPast, setBillingRatesPast] = useState<BillingRate[]>([]);
  const [billingRatesActive, setBillingRatesActive] = useState<BillingRate[]>([]);
  const [billingRatesScheduled, setBillingRatesScheduled] = useState<BillingRate[]>([]);

  //Invoice Fields
  const [isInvoiceSummary, setIsInvoiceSummary] = useState(false);
  const [isInvoiceDetail, setIsInvoiceDetail] = useState(false);
  const [isCdcReport, setIsCdcReport] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [paymentBy, setPaymentBy] = useState('');
  const [invoiceDueDate, setInvoiceDueDate] = useState(new Date());
  const [groupInvoiceSummaryBy, setGroupInvoiceSummaryBy] = useState('');
  const [groupInvoiceDetailBy, setGroupInvoiceDetailBy] = useState('');
  const [invoicingNotes, setInvoicingNotes] = useState('');

  // Billing Specialists
  const [billingSpecialists, setBillingSpecialists] = useState<User[]>([]);
  const [billingSpecialistsOptions, setBillingSpecialistsOptions] = useState<selectOption[]>([]);
  const [selectedDistrictBillingSpecialists, setSelectedDistrictBillingSpecialists] = useState<number[]>([]);
  const [selectedDistrictBillingSpecialistsOptions, setSelectedDistrictBillingSpecialistsOptions] = useState<any[]>([]);

  //ESY Tab Hooks
  const [esyStartEnabled, setEsyStartEnabled] = useState(false);
  const [esyStartDate, setEsyStartDate] = useState<Date | null>(null);
  const [esyEndEnabled, setEsyEndEnabled] = useState(false);
  const [esyEndDate, setEsyEndDate] = useState<Date | null>(null);

  //District Data
  const districtData = useAsync({
    promiseFn: fetchDistrict,
    id,
    onResolve: (r) => {
      setDistrictId(r.data.district.districtId);
      setName(r.data.district.name);
      setKellyCustomerNumber(r.data.district.kellyCustomerNumber);
      setVisible(r.data.district.isVisible);
      setStateId(Number(r.data.district.state.stateId));
      setDivisionId(Number(r.data.district.division.divisionId));
      setAddress1(r.data.district.address1);
      setAddress2(r.data.district.address2);
      setCity(r.data.district.city);
      setZipcode(r.data.district.zipcode);
      setPhone(r.data.district.phone);
      setAllowsDistrictTime(r.data.district.allowsDistrictTime);
      setRequiresMonthlyCaseNotes(r.data.district.requiresMonthlyCaseNotes);
      setQuickbooksName(r.data.district.quickbooksName);
      //setTargetHours(Number(r.data.district.targetHours ?? 450 / 60));
      setDailyAllowance(r.data.district.dailyAllowance);
      setDailyOverageAllowance(r.data.district.dailyOverageAllowance);
      setOverageAllowed(r.data.district.dailyOverageAllowance > 0 ? true : false);
      setUnlimitedOverageAllowed(r.data.district.unlimitedOverageAllowed);
      setIsOverageUnlimited(r.data.district.unlimitedOverageAllowed && dailyOverageAllowance === 0);
      setFixedTargetHours(r.data.district.fixedTargetHours);
      setContractReceivedDate(r.data.district.contractReceivedDate ?? new Date());
      setContractExpirationYear(r.data.district.contractExpirationYear ?? new Date());
      setIsMultiYearContract(r.data.district.isMultiYearContract);
      setIsInvoiceDetail(r.data.district.isInvoiceDetail ?? false);
      setIsInvoiceSummary(r.data.district.isInvoiceSummary);
      setIsCdcReport(r.data.district.isCdcReport);
      setDeliveryMethod(r.data.district.deliveryMethod);
      setPaymentBy(r.data.district.paymentBy);
      setInvoiceDueDate(r.data.district.invoiceDueDate ?? new Date());
      setGroupInvoiceDetailBy(r.data.district.groupInvoiceDetailBy);
      setGroupInvoiceSummaryBy(r.data.district.groupInvoiceSummaryBy);
      setInvoicingNotes(r.data.district.notes);
      setContactsData(r.data.district.contacts);
      setPrograms(r.data.district.programs);
      setEsyStartEnabled(moment(r.data.district.esyStartDate).isValid());
      setEsyStartDate(r.data.district.esyStartDate ?? null);
      setEsyEndEnabled(moment(r.data.district.esyEndDate).isValid());
      setEsyEndDate(r.data.district.esyEndDate ?? null);

      setReadOnly(hasRole(userRoles!, Role.Accounting));
      setIsOperationsUser(hasRole(userRoles!, Role.Operations));
      setIsAdmin(hasRole(userRoles!, Role.Admin));
      setIsBillingSpecialist(hasRole(userRoles!, Role.BillingSpecialist));
    },
  }) ?? { data: {} };

  const statesData = useAsync({ promiseFn: getStates }); //Get U.S. states
  const divisionsData = useAsync({ promiseFn: getDivisions }); //Get divisions

  const allDirectorsData = useAsync({
    promiseFn: getDirectors,
    onResolve: (response) => {
      const directors: User[] = response.data;
      setAllDirectors(directors);
    }
  });

  const disciplineGroupsData = useAsync({
    promiseFn: getDisciplineGroups,
    onResolve: (response) => {
      setDisciplineGroups(response.data.disciplineGroups);
    }
  });

  const billingSpecialistsData = useAsync({
    promiseFn: fetchBillingSpecialists,
    id,
    onResolve: (r) => {
      const b: User[] = r.data;
      const options: selectOption[] = [];
      setBillingSpecialists(b);

      //Loop through the disciplines to generate the options for the multi select
      (b.map((user) => (
        options.push({ value: user.userId ?? 0, label: `${user.lastName}, ${user.firstName}` })
      )));
      options.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      setBillingSpecialistsOptions(options);
    },
  });

  const districtBillingSpecialistsData = useAsync({
    promiseFn: fetchDistrictBillingSpecialists,
    id,
    onResolve: (r) => {
      const b: DistrictBillingSpecialist[] = r.data;
      const options: any[] = [];
      const ids: number[] = [];

      //Loop through the disciplines to generate the options for the multi select
      b.map((user) => options.push({ value: user.userId ?? 0, label: `${user.user?.lastName}, ${user.user?.firstName}` }));
      setSelectedDistrictBillingSpecialistsOptions(options);

      //Generate our specialists array
      b.map((user) => ids.push(user.userId));
      setSelectedDistrictBillingSpecialists(ids);
    },
  });

  const handleDistrictBillingSpecialistsChange = (options: any) => {
    const newBs: number[] = [];
    const ops: selectOption[] = [];

    (options.map((opt: any) => {
      const bs = billingSpecialists.filter((bs) => opt.value === bs.userId)[0];
      newBs.push(bs.userId!);
      ops.push({ value: opt.value, label: opt.label });
      return true;
    }));

    setSelectedDistrictBillingSpecialists(newBs);
    setSelectedDistrictBillingSpecialistsOptions(ops);
  }

  useAsync({
    promiseFn: fetchTherapyTeam,
    id,
    onResolve: (response) => setTherapyTeam(response.data)
  });

  /**
   * District saving async functions
   */
  const saveDistrict = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    return await updateDistrict(districtId, {
      name: name,
      isVisible: visible,
      stateId: stateId,
      kellyCustomerNumber: kellyCustomerNumber,
      divisionId: divisionId,
      address1: address1,
      address2: address2,
      city: city,
      zipcode: zipcode,
      phone: phone,
      allowsDistrictTime: allowsDistrictTime,
      requiresMonthlyCaseNotes: requiresMonthlyCaseNotes,
      quickbooksName: quickbooksName,
      //targetHours: targetHours,
      dailyAllowance: dailyAllowance,
      dailyOverageAllowance: dailyOverageAllowance,
      unlimitedOverageAllowed: unlimitedOverageAllowed,
      fixedTargetHours: fixedTargetHours,
      contractReceivedDate: contractReceivedDate,
      contractExpirationYear: contractExpirationYear,
      isMultiYearContract: isMultiYearContract,
      isInvoiceDetail: isInvoiceDetail,
      isInvoiceSummary: isInvoiceSummary,
      isCdcReport: isCdcReport,
      deliveryMethod: deliveryMethod,
      paymentBy: paymentBy,
      invoiceDueDate: invoiceDueDate,
      groupInvoiceDetailBy: groupInvoiceDetailBy,
      groupInvoiceSummaryBy: groupInvoiceSummaryBy,
      notes: invoicingNotes,
      contacts: [],
      billingSpecialistIds: selectedDistrictBillingSpecialists,
      esyStartDate: esyStartDate ? new Date(esyStartDate).toLocaleDateString() : null,
      esyEndDate: esyEndDate ? new Date(esyEndDate).toLocaleDateString() : null
    });
  };

  const save = useAsync({
    deferFn: saveDistrict,
    onResolve: () => {
      setHasChanges(false);
      window.scrollTo(0, 0);
    },
    onReject: () => {
      window.scrollTo(0, 0);
    },
  });

  //Remove district run
  const removeDistrict = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    return await deleteDistrict(districtId);
  };

  //Remove district async
  const remove = useAsync({
    deferFn: removeDistrict,
    onResolve: () => history.push('/admin/districts', { message: `"${name}" removed successfully!` })
  });

  //Get calendar items for this district
  useAsync({
    promiseFn: fetchCalendarItemsByDistrictId,
    id,
    onResolve: (r) => {
      //Generate calendar items
      let items: CalendarItem[] = [];
      let events: EventSource[] = [];
      if (r.data.calendarItems && r.data.calendarItems.length > 0) {
        r.data.calendarItems.map((c, i) => {
          const startDate = new Date(c.startDate);
          const endDate = new Date(c.endDate);
          const item: CalendarItem = {
            calendarItemId: Number(c.calendarItemId),
            title: c.title,
            startDate: startDate,
            endDate: endDate,
            colorCode: c.colorCode,
            notes: c.notes,
            isVisible: true
          };
          items.push(item);

          const event: EventSource = {
            id: c.calendarItemId?.toString() ?? '0',
            start: startDate,
            end: endDate,
            title: item.title,
            backgroundColor: calendarColorCodes[item.colorCode].hexCode,
            notes: c.notes,
            allDay: moment(c.startDate).isSame(c.endDate),
          };
          events.push(event);
          return true;
        });

        setCalendarItems(items);
        setCalendarEvents(events);

        return true;
      }
    }
  });

  //Get billing rates for this district
  useAsync({
    promiseFn: fetchBillingRatesByDistrictId,
    id,
    onResolve: (r) => {
      //Sort the billing rates
      let past = [], scheduled = [], active = [], pending = [];
      if (r.data && r.data.length > 0) {
        for (let i = 0; i < r.data.length; i++) {
          const br: BillingRate = r.data[i];
          const isApproved = br.approvedDate !== null;
          const startDate: Date = new Date(br.startDate);
          const endDate: Date = new Date(br.endDate);
          const todaysDate: Date = new Date();
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          todaysDate.setHours(0, 0, 0, 0);

          if (!isApproved) {
            pending.push(r.data[i]);
          } else if ((startDate < todaysDate && endDate < todaysDate) || !br.isVisible) {
            past.push(r.data[i]);
          } else if (startDate <= todaysDate && endDate >= todaysDate) {
            active.push(br);
          } else if (startDate > todaysDate && endDate > todaysDate) {
            scheduled.push(br);
          }
        }

        //Set the billing rate states
        setBillingRatesPending(pending);
        setBillingRatesPast(past);
        setBillingRatesActive(active);
        setBillingRatesScheduled(scheduled);
      }
    }
  });

  //Get all disciplines
  useAsync({
    promiseFn: fetchDisciplines,
    id,
    onResolve: (r) => {
      const dd: DirectorDiscipline = { ...r.data.disciplines[0], districtId: districtId ?? 0 };
      setDisciplines(r.data.disciplines);
      setAddDirectorDiscipline(dd);
      setAddDirectorDisciplines([]);
      //setAddDirectorDisciplineGroups([]);
    }
  });

  /**
   * Director Modal and Data
   */
  useAsync({
    promiseFn: fetchDirectorsByDistrictId,
    id,
    onResolve: (r) => setDirectorsData(r.data.directors)
  });

  /**
   * Performs the save functionality after the add director modal has been closed
   * This is an async function as we are calling async...await within this function
   * This could potentially be synchronous and still function as expected but would
   * have to change the awaits to Promise.then()
   */
  const addDirectorModalSave = async () => {
    //Ensure the currently selected discipline is added to the list
    handleAddDirectorDisciplineClick();

    //Build our director request object
    const directorRequest: DirectorRequest = {
      name: addDirectorName,
      directorDisciplines: addDirectorDisciplines,
      disciplineGroups: addDirectorDisciplineGroups,
      districtId: districtId ?? 0,
      userId: addDirectorUserId,
      isVisible: true,
    };

    //If the addDirectorId is greater than 0 then we are updating an existing
    //director record
    if (directorId) {
      //Update the director
      await updateDirector(directorId, directorRequest);

      //Get the new director's list
      const directorsResponse = await getDirectorsByDistrictId(districtId ?? 0);

      //Update the UI
      setDirectorsData(current => [...directorsResponse.data.directors]);

      //Close the modal and exit the function
      setOpenAddDirectorModal(false);
      return;
    }

    //If we fall to here, then we are adding, not updating, a director
    //Run the request
    const data = await createDirector(directorRequest);

    //Get the director object returned from the API
    const director: Director = data.data.director;

    //Add the disciplines to the director object
    director.directorDisciplines = addDirectorDisciplines;

    //Update our hooks
    setDirectorsData(current => [...directorsData, director]);
    setDirectorId(undefined);

    //Close the modal
    setOpenAddDirectorModal(false);
  }

  /**
   * Performs the director delete
   */
  const deleteDirectorSaveRun = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    return await deleteDirector(directorId);
  }

  /**
   * Async hook for function above
   */
  const removeDirector = useAsync({
    deferFn: deleteDirectorSaveRun,
    onResolve: (data: any) => {
      let directors: Director[] = directorsData.filter((c) => c.directorId !== directorId);
      setDirectorsData(directors);
      setOpenAddDirectorModal(false);
      setDirectorId(undefined);
    },
    onReject: (data) => { console.log(data) }
  });

  /**
   * Loads a director from the API and sets all of the modal hooks
   * @param directorId number
   */
  const loadDirector = (directorId: number = 0): void => {
    const loadDirectorRequest = async (id: number) => {
      const response = await getDirectorById(id);

      if (response.status >= 200 && response.status < 400) {
        const director: Director = response.data.director;
        setDirectorId(director.directorId);
        setAddDirectorName(director.name);
        const disciplines: DirectorDiscipline[] = [];
        director.directorDisciplines.forEach(d => {
          disciplines.push({
            name: d.discipline?.name ?? '',
            directorId: d.directorId,
            districtId: d.districtId,
            //discipline: d.discipline,
            disciplineId: d.disciplineId,
          } as DirectorDiscipline)
        });

        const disciplineGroups: DisciplineGroup[] = [];
        if (director.disciplineGroups !== null && director.disciplineGroups !== undefined) {
          director.disciplineGroups?.forEach(d => {
            disciplineGroups.push({
              name: d.name ?? '',
              isVisible: true,
              directorId: directorId,
              disciplineGroupId: d.disciplineGroupId,
            } as DisciplineGroup)
          });
        }

        setAddDirectorDisciplines(disciplines);
        setAddDirectorDisciplineGroups(disciplineGroups);
        setAddDirectorUserId(director.userId ?? 0);
      }
    }

    loadDirectorRequest(directorId);
  }

  /**
   * Contacts Modal and data
   */
  const addContactSave = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    const contactRequest: ContactRequest = {
      districtId: districtId,
      name: addContactName,
      lastName: addContactLastName,
      isInvoice: addContactIsInvoice,
      title: addContactTitle,
      email: addContactEmail,
      phone: addContactPhone,
      fax: addContactFax,
      address1: addContactAddress1,
      address2: addContactAddress2,
      city: addContactCity,
      stateId: Number(addContactStateId) ?? 0,
      zipcode: Number(addContactZipcode) ?? 0,
      notes: addContactNotes
    };

    //Reset modal fields
    setAddContactName('');
    setAddContactLastName('');
    setAddContactIsInvoice(false);
    setAddContactTitle('');
    setAddContactEmail('');
    setAddContactPhone('');
    setAddContactFax('');
    setAddContactAddress1('');
    setAddContactAddress2('');
    setAddContactCity('');
    setAddContactStateId('');
    setAddContactZipcode('');
    setAddContactNotes('');

    //If we have a conatct ID set, then we are updating the contact
    if (addContactId) {
      contactRequest.contactId = addContactId;
      //setAddContactId(0);
      return await updateContact(contactRequest.contactId, contactRequest);
    }

    return await createContact(contactRequest);
  }

  const addContact = useAsync({
    deferFn: addContactSave,
    onResolve: (data: any) => {
      const isUpdating: boolean = data.data === '';
      const contact: Contact = data.data.contact ?? JSON.parse(data.config.data);
      let contacts: Contact[] = contactsData;

      if (isUpdating) { //Updating
        contacts.map((c, i) => {
          //Check if this contact is the one being updated
          if (c.contactId === addContactId) contacts[i] = contact;

          return true;
        });
      } else { //Adding
        contacts.push(contact);
      }

      setContactsData(contacts);

      //Hide modal
      setOpenAddContactModal(false);
    },
    onReject: (data) => { console.log(data); return false; }
  });

  const initEditContactModal = (contactId: number) => {
    //Find the contact with the given ID
    let contact: any = contactsData?.find(c => c.contactId === contactId);
    setAddContactId(Number(contact?.contactId));
    setAddContactIsInvoice(contact?.isInvoice);
    setAddContactName(contact?.name);
    setAddContactLastName(contact?.lastName);
    setAddContactTitle(contact?.title);
    setAddContactEmail(contact?.email);
    setAddContactPhone(contact?.phone);
    setAddContactFax(contact?.fax);
    setAddContactAddress1(contact?.address1);
    setAddContactAddress2(contact?.address2);
    setAddContactCity(contact?.city);
    setAddContactZipcode(contact?.zipcode.toString());
    setAddContactStateId(contact?.stateId.toString());
    setAddContactNotes(contact?.notes);
    setOpenAddContactModal(true);
  };

  const deleteContactSaveRun = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    return await deleteContact(addContactId);
  }

  const removeContact = useAsync({
    deferFn: deleteContactSaveRun,
    onResolve: (data: any) => {
      let contacts: Contact[] = contactsData.filter((c) => c.contactId !== addContactId);
      setContactsData(contacts);
      setOpenAddContactModal(false);
    },
    onReject: (data) => { console.log(data) }
  });


  /**
   * Calendar Modal and Data
   */
  const addCalendarItemSave = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    const calendarItemRequest: CalendarItemRequest = {
      districtId: districtId,
      title: addCalendarItemTitle,
      startDate: addCalendarItemStartDate,
      endDate: addCalendarItemEndDate,
      colorCode: addCalendarItemColorCode,
      isVisible: addCalendarItemIsVisible,
      notes: addCalendarItemNotes === '' ? 'No notes' : addCalendarItemNotes
    };

    return await createCalendarItem(calendarItemRequest);
  }

  const addCalendarItem = useAsync({
    deferFn: addCalendarItemSave,
    onResolve: (data) => {
      const calendarItem: CalendarItem = {
        districtId: districtId,
        title: data.data.calendarItem.title,
        startDate: data.data.calendarItem.startDate,
        endDate: data.data.calendarItem.endDate,
        isVisible: data.data.calendarItem.isVisible,
        colorCode: data.data.calendarItem.colorCode,
        notes: data.data.calendarItem.notes === '' ? 'No notes' : data.data.calendarItem.notes
      };

      const calendarEvent: EventSource = {
        id: data.data.calendarItem.calendarItemId?.toString() ?? '0',
        start: data.data.calendarItem.startDate,
        end: data.data.calendarItem.endDate,
        title: data.data.calendarItem.title,
        backgroundColor: calendarColorCodes[data.data.calendarItem.colorCode].hexCode,
        notes: data.data.calendarItem.notes === '' ? 'No notes' : data.data.calendarItem.notes,
        allDay: moment(data.data.calendarItem.startDate).isSame(data.data.calendarItem.endDate),
      };

      //Add to calendar
      setCalendarItems(current => [...calendarItems, calendarItem]);
      setCalendarEvents(current => [...calendarEvents, calendarEvent]);
      setOpenAddCalendarModal(false);
    },
    onReject: (data) => { console.log(data) }
  });

  const initEditCalendarItemModal = (startDate: Date, endDate: Date) => {
    //Set the times to noon so they stay within the date range
    startDate.setHours(12);
    endDate.setHours(-12);

    setAddCalendarItemTitle('');
    setAddCalendarItemColorCode(-1);
    setAddCalendarItemStartDate(startDate);
    setAddCalendarItemEndDate(endDate);
    setAddCalendarItemNotes('');
    setOpenAddCalendarModal(true);
  };

  const deleteCalendarItemSaveRun = async () => {
    if (!districtId) {
      return Promise.reject('Missing districtId.');
    }

    return await deleteCalendarItem(addCalendarItemId);
  }

  const removeCalendarItem = useAsync({
    deferFn: deleteCalendarItemSaveRun,
    onResolve: (data: any) => {
      let items: CalendarItem[] = calendarItems.filter((c) => c.calendarItemId !== addCalendarItemId);
      let events: EventSource[] = calendarEvents.filter((e) => Number(e.id) !== addCalendarItemId);
      setCalendarItems(items);
      setCalendarEvents(events);
      setOpenCalendarNotesModal(false);
    },
    onReject: (data) => { console.log(data) }
  });

  /**
   * Renders the calendar event content
   * @param info 
   * @returns 
   */
  const renderEventContent = (info: EventContentArg) => {
    return (
      <>
        <h4 data-tip={info.event.title}>
          {info.event.extendedProps.notes?.toLowerCase() !== "no notes" && (
            <PencilIcon
              className='text-white w-3.5 inline-block ml-0.5 mr-0.5'
            />
          )}
          {info.event.title}
        </h4>
        <ReactTooltip />
      </>
    )
  }

  /**
   * Click handler for adding a discipline to the list
   */
  const handleAddDirectorDisciplineClick = () => {
    if (addDirectorDiscipline !== undefined) {
      if (addDirectorDisciplines.filter((d) => d.disciplineId === addDirectorDiscipline?.disciplineId).length === 0) {
        setAddDirectorDisciplines(current => [...current, addDirectorDiscipline ?? { name: '', disciplineId: 0, districtId: districtId ?? 0 }]);
        setAddDirectorDiscipline(undefined);
        setAddDirectorDisciplineGroup(undefined);
      }
    }

    if (addDirectorDisciplineGroup !== undefined) {
      if (addDirectorDisciplineGroups.filter((d) => d.disciplineGroupId === addDirectorDisciplineGroup?.disciplineGroupId).length === 0) {
        setAddDirectorDisciplineGroups(current => [...addDirectorDisciplineGroups, addDirectorDisciplineGroup ?? { name: '', disciplineGroupId: 0, disciplineId: 0, districtId: districtId ?? 0 }]);
        setAddDirectorDiscipline(undefined);
        setAddDirectorDisciplineGroup(undefined);
      }
    }
  }

  /**
   * If the esy start or end dates are unchecked, then reset the start dates as well
   */
  // useEffect(() => {
  //   if (!esyStartEnabled) {
  //     setEsyStartDate(null);
  //   }
  // }, [esyStartEnabled]);

  const isESYDateMismatch = (): boolean => {
    return (!!esyStartDate && !esyEndDate) || (!esyStartDate && !!esyEndDate);
  };


  return (
    <Fragment>
      <Layout
        isLoading={
          districtData.isPending ||
          statesData.isPending ||
          divisionsData.isPending ||
          allDirectorsData.isPending ||
          save.isPending ||
          remove.isPending ||
          addContact.isPending ||
          removeContact.isPending ||
          addCalendarItem.isPending ||
          removeCalendarItem.isPending ||
          removeDirector.isPending ||
          districtBillingSpecialistsData.isPending ||
          billingSpecialistsData.isPending ||
          disciplineGroupsData.isPending
        }
      >
        <Fragment>
          <Prompt
            when={hasChanges}
            message={() => ConfirmPendingChanges}
          />

          {state?.message && <Notification>{state?.message}</Notification>}
          <ResponseStatus state={save} />
          <ResponseStatus state={remove} />

          {/* Modal for Add/Edit Director */}
          <EditModal
            open={openAddDirectorModal}
            saveText={`${directorId ? `Update` : `Add`} Director`}
            saveColor='bg-ptsgreen-normal hover:bg-ptsgreen-normal'
            onSave={addDirectorModalSave}
            onRemove={directorId ? () => {
              setDirectorId(directorId);
              removeDirector.run()
            } : undefined}
            removeText={directorId ? `Remove` : undefined}
            removeColor='bg-red-600 hover:bg-red-700'
            onCancel={() => { setOpenAddDirectorModal(false) }}
            id={directorId}
            allowSave={addDirectorDisciplines.length > 0 || addDirectorDisciplineGroups.length > 0}
          >
            <div>
              <div className='w-full'>
                <label className={`primary ${directorId ? 'hidden' : ''}`}>Director Name</label>
                <select
                  className={`w-full mr-4 ${directorId ? 'hidden' : ''}`}
                  onChange={(e) => { setAddDirectorUserId(Number(e.target.value)); changesMade(); }}
                  value={addDirectorUserId}
                >
                  <option key="all-directors-1" value="-1">Select A Director...</option>
                  {allDirectors.sort((a, b) => {
                    return a.lastName.localeCompare(b.lastName);
                  }).map((d: User) => (
                    <option
                      key={`all-directors-${d.userId}`}
                      value={d.userId}
                      defaultValue={addDirectorUserId}
                    >{`${d.lastName}, ${d.firstName}`}</option>
                  ))}
                </select>
              </div>
              <hr className={`mb-2 mt-3 ${directorId ? 'hidden' : ''}`}></hr>

              <label className='mb-3 text-lg font-bold'>Disciplines:</label>
              <div className='flex flex-1 mb-2'>
                <SelectMulti
                  options={disciplines!.sort((a, b) => a.name.localeCompare(b.name)).map((d) => ({ value: d.disciplineId, label: d.name } as selectOption)) as selectOption[]}
                  id="disciplinesOptions"
                  className='block w-full primary'
                  isMulti={true}
                  key={`ds-multi-${addDirectorDisciplines?.filter((d) => d.disciplineId !== 0).map(d => d.disciplineId).length}`}
                  isClearable={true}
                  defaultValue={addDirectorDisciplines?.filter((d) => d.disciplineId !== 0).map(d => ({ value: d.disciplineId, label: d.name } as selectOption))}
                  onChange={(e: MultiValue<selectOption>) => {
                    const discs: DirectorDiscipline[] = [];
                    (e.map((opt: any) => {
                      let disc: DirectorDiscipline = { name: opt.label, disciplineId: opt.value, districtId: districtId ?? 0 };
                      discs.push(disc);
                      return true;
                    }));
                    setAddDirectorDisciplines(discs);
                  }}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              <label className='mb-3 text-lg font-bold'>Discipline Groups:</label>
              <div className='flex flex-1 mb-2'>
                <SelectMulti
                  options={disciplineGroups!.map((d) => ({ value: d.disciplineGroupId, label: d.name } as selectOption)) as selectOption[]}
                  id="disciplineGroupsOptions"
                  className='block w-full primary'
                  isMulti={true}
                  key={`ds-multi-${addDirectorDisciplineGroups?.filter((d) => d.disciplineGroupId !== 0).map(d => d.disciplineGroupId).length}`}
                  isClearable={true}
                  defaultValue={addDirectorDisciplineGroups?.filter((d) => d.disciplineGroupId !== 0).map(d => ({ value: d.disciplineGroupId, label: d.name } as selectOption))}
                  onChange={(e: MultiValue<selectOption>) => {
                    const discs: DisciplineGroup[] = [];
                    (e.map((opt: any) => {
                      let disc: DisciplineGroup =
                        {
                          disciplineGroupId: opt.value,
                          name: opt.label,
                          isVisible: true,
                        } as DisciplineGroup

                      discs.push(disc);
                      return true;
                    }));
                    setAddDirectorDisciplineGroups(discs);
                  }}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            </div>
          </EditModal>

          {/* Modal for Add Contact */}
          <EditModal
            open={openAddContactModal}
            saveText={`${(!addContactId || addContactId === 0 ? 'Add' : 'Update')} Contact`}
            onSave={() => addContact.run()}
            onCancel={() => setOpenAddContactModal(false)}
            onRemove={() => removeContact.run()}
            id={addContactId}
            allowSave={
              addContactName !== '' &&
              addContactLastName !== '' &&
              addContactTitle !== '' &&
              addContactAddress1 !== '' &&
              addContactCity !== ''
            }
            allowRemove={addContactId > 0}
          >
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-name'
                  label='First Name'
                  onChange={(e) => { setAddContactName(e.target.value); changesMade(); }}
                  value={addContactName}
                  minLength={1}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-last-name'
                  label='Last Name'
                  onChange={(e) => { setAddContactLastName(e.target.value); changesMade(); }}
                  value={addContactLastName}
                  minLength={1}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <input
                  name='add-contact-is-invoice'
                  type='checkbox'
                  className='primary-checkbox'
                  checked={addContactIsInvoice}
                  onChange={(e) => { setAddContactIsInvoice(e.currentTarget.checked); changesMade(); }}
                />
                <label htmlFor='add-contact-is-invoice' className='px-2 primary'>Invoice Contact</label>
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-title'
                  label='Title'
                  onChange={(e) => { setAddContactTitle(e.target.value); changesMade(); }}
                  value={addContactTitle}
                  minLength={1}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-email'
                  label='Email'
                  onChange={(e) => { setAddContactEmail(e.target.value); changesMade(); }}
                  value={addContactEmail}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <ReactPhoneInput
                  country={'us'}
                  onChange={(phone) => { setAddContactPhone(phone); changesMade(); }}
                  value={addContactPhone}
                  disableDropdown={true}
                  disableSearchIcon={true}
                  disableCountryCode={true}
                  enableAreaCodes={true}
                  placeholder={'(555) 555 5555'}
                  specialLabel={'Phone'}
                  onlyCountries={["us"]}
                  autocompleteSearch={false}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <ReactPhoneInput
                  country={'us'}
                  onChange={(fax) => { setAddContactFax(fax); changesMade(); }}
                  value={addContactFax}
                  disableDropdown={true}
                  disableSearchIcon={true}
                  disableCountryCode={true}
                  enableAreaCodes={true}
                  placeholder={'(555) 555 5555'}
                  specialLabel={'Fax'}
                  onlyCountries={["us"]}
                  autocompleteSearch={false}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-address-1'
                  label='Address 1'
                  onChange={(e) => { setAddContactAddress1(e.target.value); changesMade(); }}
                  value={addContactAddress1}
                  minLength={1}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-address-2'
                  label='Address 2'
                  onChange={(e) => { setAddContactAddress2(e.target.value); changesMade(); }}
                  value={addContactAddress2}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-city'
                  label='City'
                  onChange={(e) => { setAddContactCity(e.target.value); changesMade(); }}
                  value={addContactCity}
                  minLength={1}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <label className='primary'>State<span className='pl-1 text-red-500'>*</span></label>
                <select
                  name="contact-state"
                  onChange={(e) => { setAddContactStateId(e.target.value); changesMade(); }}
                  className='block mt-1 primary'
                  defaultValue={addContactStateId}
                >
                  {statesData.data?.data.states.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((s) => (
                    <option key={`contactState_${s.stateId}`} value={s.stateId}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-zipcode'
                  label='Zip Code'
                  onChange={(e) => { setAddContactZipcode(e.target.value); changesMade(); }}
                  value={addContactZipcode}
                />
              </div>
            </div>
            <div className='flex mb-3 space-x-3'>
              <div className='w-full'>
                <FieldInput
                  name='contact-notes'
                  label='Notes'
                  onChange={(e) => { setAddContactNotes(e.target.value); changesMade(); }}
                  value={addContactNotes}
                />
              </div>
            </div>
          </EditModal>

          {/* Modal for Add Calendar Item */}
          <EditModal
            open={openAddCalendarModal}
            saveText={'Save Calendar Item'}
            onSave={() => addCalendarItem.run()}
            onCancel={() => setOpenAddCalendarModal(false)}
            id={addCalendarItemId}
            allowSave={!readOnly && addCalendarItemTitle !== '' && addCalendarItemColorCode > -1}
          >
            <div>
              <div className='col-span-6 sm:col-span-6'>
                <label htmlFor='add-calendar-item-title' className='primary'>Title</label>
                <select
                  name="add-calendar-item-title"
                  onChange={(e) => {
                    setAddCalendarItemColorCode(Number(e.target.value));
                    setAddCalendarItemTitle(e.target.options[e.target.selectedIndex].text);
                    changesMade();
                  }}
                  className='block mt-1 primary'
                  value={addCalendarItemColorCode}
                  disabled={readOnly || isOperationsUser}
                >
                  <option value='-1'>-- Choose Title --</option>
                  <option key={`calendarColorCode_0`} value={0}>Non-Billable Days</option>
                  <option key={`calendarColorCode_1`} value={1}>Student Non-Billable Days</option>
                  <option key={`calendarColorCode_2`} value={2}>School Year Start End Dates for Teachers/Therapists</option>
                  <option key={`calendarColorCode_3`} value={3}>School Year Start End Dates for Students</option>
                  <option key={`calendarColorCode_4`} value={4}>ESY Start End Dates for Teachers/Therapists</option>
                  <option key={`calendarColorCode_5`} value={5}>ESY Start End Dates for Students</option>
                  <option key={`calendarColorCode_6`} value={6}>Exception Dates (like snow days)</option>
                </select>
              </div>
              <div>
                <label htmlFor='add-calendar-item-start-date' className='primary'>Start Date</label>
              </div>
              <div>
                <input
                  name="add-calendar-item-start-date"
                  type="text"
                  value={addCalendarItemStartDate.toLocaleDateString('en-us', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                  disabled
                />
              </div>
              <div>
                <label htmlFor='add-calendar-item-end-date' className='primary'>End Date</label>
              </div>
              <div>
                <input
                  name="add-calendar-item-end-date"
                  type="text"
                  value={addCalendarItemEndDate.toLocaleDateString('en-us', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                  disabled
                />
              </div>
              <div>
                <label htmlFor="add-calendar-item-notes">Notes:</label>
              </div>
              <div>
                <textarea
                  name="add-calendar-item-notes"
                  onChange={(e) => { setAddCalendarItemNotes(e.target.value); changesMade(); }}
                  value={addCalendarItemNotes}
                />
              </div>
            </div>
          </EditModal>

          {/* Modal for Calendar Notes */}
          <EditModal
            open={openCalendarNotesModal}
            id={addCalendarItemId}
            allowRemove={!readOnly}
            onRemove={() => removeCalendarItem.run()}
            onCancel={() => setOpenCalendarNotesModal(false)}
          >
            <div className='flex space-x-3'>
              <h3>{calendarNotes}</h3>
            </div>
          </EditModal>

          {/* Primary Profile Form */}
          <div>
            <div className='grid grid-cols-6 gap-6 mb-6'>
              <div className='col-span-6 sm:col-span-6'>
                <div className="flex items-center space-x-4">
                  <label className='primary whitespace-nowrap'>District ID: {districtId}</label>
                  <label className='primary whitespace-nowrap'>Kelly Customer Number:</label>
                  {
                    (isAdmin || isBillingSpecialist) ? (<div className='w-1/4'><FieldInput

                      name='kelly-customer-number'
                      onChange={(e) => { setKellyCustomerNumber(e.target.value); changesMade(); }}
                      value={kellyCustomerNumber}
                    /></div>) : (<label>{kellyCustomerNumber === null ? null : kellyCustomerNumber}</label>)
                  }
                </div>
              </div>
            </div>
            <div className='grid grid-cols-6 gap-6 mb-6'>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='district'
                  label='Name'
                  minLength={1}
                  maxLength={70}
                  onChange={(e) => { setName(e.target.value); changesMade(); }}
                  value={name}
                  disabled={readOnly || isOperationsUser}
                  readonly={readOnly || isOperationsUser}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <div className='flex items-start'>
                  <div className='flex items-center h-5'>
                    <input
                      id='is-visible'
                      name='is-visible'
                      type='checkbox'
                      className='primary-checkbox'
                      checked={visible}
                      onChange={(e) => { setVisible(e.currentTarget.checked); changesMade(); }}
                      disabled={readOnly || isOperationsUser}
                      readOnly={readOnly || isOperationsUser}
                    />
                  </div>
                  <div className='ml-3 text-sm'>
                    <label htmlFor='is-visible' className='primary'>
                      Is Visible
                    </label>
                    <p className='text-gray-500'>
                      When checked, this district will be available in the
                      timesheet entry flow.
                    </p>
                  </div>
                </div>
              </div>

              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='address-field'
                  label='Address 1'
                  onChange={(e) => { setAddress1(e.target.value); changesMade(); }}
                  value={address1}
                  disabled={readOnly || isOperationsUser}
                  readonly={readOnly || isOperationsUser}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='address2-field'
                  label='Address 2'
                  onChange={(e) => { setAddress2(e.target.value); changesMade(); }}
                  value={address2}
                  disabled={readOnly || isOperationsUser}
                  readonly={readOnly || isOperationsUser}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='city-field'
                  label='City'
                  onChange={(e) => { setCity(e.target.value); changesMade(); }}
                  value={city}
                  disabled={readOnly || isOperationsUser}
                  readonly={readOnly || isOperationsUser}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  State<span className='pl-1 text-red-500'>*</span>
                </label>
                <select
                  onChange={(e) => { setStateId(Number(e.target.value)); changesMade(); }}
                  className='block mt-1 primary'
                  value={stateId}
                  disabled={readOnly || isOperationsUser}
                >
                  {statesData.data?.data.states.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((s) => (
                    <option key={`state_${s.stateId}`} value={s.stateId}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <FieldInput
                  name='zipcode-field'
                  label='Zip Code'
                  onChange={(e) => { setZipcode(e.target.value); changesMade(); }}
                  value={zipcode}
                  disabled={readOnly || isOperationsUser}
                  readonly={readOnly || isOperationsUser}
                />
              </div>
              <div className='col-span-6 sm:col-span-6'>
                <ReactPhoneInput
                  country={'us'}
                  onChange={(phone) => { setPhone(phone); changesMade(); }}
                  value={phone}
                  disableDropdown={true}
                  disableSearchIcon={true}
                  disableCountryCode={true}
                  enableAreaCodes={true}
                  placeholder={'(555) 555 5555'}
                  specialLabel={'Phone'}
                  onlyCountries={["us"]}
                  disabled={readOnly || isOperationsUser}
                />
              </div>

              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  Division<span className='pl-1 text-red-500'>*</span>
                </label>
                <select
                  onChange={(e) => { setDivisionId(Number(e.target.value)); changesMade(); }}
                  className='block mt-1 primary'
                  value={divisionId}
                  disabled={readOnly || isOperationsUser}
                >
                  {divisionsData.data?.data.divisions.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  }).map((d) => (
                    <option key={`division_${d.divisionId}`} value={d.divisionId}>{d.name}</option>
                  ))}
                </select>
              </div>

              <div className='col-span-6 sm:col-span-6'>
                <label className='primary'>
                  Billing Specialists<span className='pl-1 text-red-500'>*</span>
                </label>
                <SelectMulti
                  id="districtBillingSpecialistsOptions"
                  className='block primary'
                  isMulti={true}
                  key={`dbs-multi-${selectedDistrictBillingSpecialistsOptions.length}`}
                  options={billingSpecialistsOptions}
                  isClearable={true}
                  defaultValue={selectedDistrictBillingSpecialistsOptions}
                  onChange={(e) => handleDistrictBillingSpecialistsChange(e)}
                  isDisabled={readOnly || isOperationsUser}
                />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tab.Group>
            {/* Tab List */}
            <Tab.List className='tabs'>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    General
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Schools
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Programs
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Therapy team
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Calendar
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Billing Rates
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    AR/Invoice
                  </button>
                )}
              </Tab>
              <Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    ESY
                  </button>
                )}
              </Tab>
              {/*<Tab as={Fragment}>
                {(btn) => (
                  <button className={btn.selected ? 'active' : ''}>
                    Metrics/Rules
                  </button>
                )}
              </Tab>*/}
            </Tab.List>
            {/* Tab Content */}
            <Tab.Panels className='tabpanels'>
              {/* General */}
              <Tab.Panel>
                {/* General Tab Fields */}
                <div className="general-tab-form">
                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <div className='flex items-start'>
                      <div className='flex items-center h-5'>
                        <input
                          id='allow-district-time'
                          name='allow-district-time'
                          type='checkbox'
                          className='primary-checkbox'
                          checked={allowsDistrictTime}
                          onChange={(e) => { setAllowsDistrictTime(e.currentTarget.checked); changesMade(); }}
                          disabled={readOnly || isOperationsUser}
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <label htmlFor='allow-district-time' className='primary'>
                          Allows District Time
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <div className='flex items-start'>
                      <div className='flex items-center h-5'>
                        <input
                          id='requires-monthly-case-notes'
                          name='requires-monthly-case-notes'
                          type='checkbox'
                          className='primary-checkbox'
                          checked={requiresMonthlyCaseNotes}
                          onChange={(e) => { setRequiresMonthlyCaseNotes(e.currentTarget.checked); changesMade(); }}
                          disabled={readOnly || isOperationsUser}
                          readOnly={readOnly || isOperationsUser}
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <label htmlFor='requires-monthly-case-notes' className='primary'>
                          Requires Monthly Case Notes
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <FieldInput
                      name='quickbooks-name'
                      label='QuickBooks Name'
                      onChange={(e) => { setQuickbooksName(e.target.value); changesMade(); }}
                      value={quickbooksName}
                      disabled={readOnly || isOperationsUser}
                      readonly={readOnly || isOperationsUser}
                    />
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <div className='text-sm'>
                      <label htmlFor='daily-allowance' className='primary'>
                        Daily Allowance
                      </label>
                    </div>
                    <input
                      type='text'
                      name='daily-allowance'
                      id='daily-allowance'
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => { setDailyAllowance(Number(e.target.value)); changesMade(); }}
                      value={dailyAllowance}
                      placeholder="0"
                      className='w-full'
                      disabled={readOnly || isOperationsUser}
                      readOnly={readOnly || isOperationsUser}
                    />
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <label className='primary'>
                      Overage Allowed
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value === 'yes') {
                          setOverageAllowed(true);
                          setUnlimitedOverageAllowed(false);
                        }
                        else {
                          setOverageAllowed(false)
                          setDailyOverageAllowance(0)
                        }
                        changesMade();
                      }}
                      className='block mt-1 primary'
                      value={overageAllowed ? 'yes' : 'no'}
                      disabled={readOnly || isOperationsUser}
                    >
                      <option key="overageAllowed_Yes" value='yes'>Yes</option>
                      <option key="overageAllowed_No" value='no'>No</option>
                    </select>
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <label className='primary'>
                      Unlimited Overage Allowed
                    </label>
                    <select
                      onChange={(e) => {
                        if (e.target.value === 'yes') {
                          setUnlimitedOverageAllowed(true)
                          setDailyOverageAllowance(0)
                          setOverageAllowed(false);
                        }
                        else {
                          setUnlimitedOverageAllowed(false)
                        }
                        setIsOverageUnlimited(e.target.value === 'yes')
                        changesMade();
                      }}
                      className='block mt-1 primary'
                      value={unlimitedOverageAllowed ? 'yes' : 'no'}
                      disabled={isOperationsUser || readOnly}
                    >
                      <option key="overageAllowed_Yes" value='yes'>Yes</option>
                      <option key="overageAllowed_No" value='no'>No</option>
                    </select>
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <div className='text-sm'>
                      <label htmlFor='requires-monthly-case-notes' className='primary'>
                        Overage Allowance
                      </label>
                    </div>
                    <input
                      type='text'
                      name='overage-allowance'
                      id="overage-allowance"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => { setDailyOverageAllowance(Number(e.target.value)); changesMade(); }}
                      value={dailyOverageAllowance}
                      placeholder="0"
                      className={`w-full ${(!overageAllowed && unlimitedOverageAllowed) ? 'text-gray-500' : ''}`}
                      disabled={unlimitedOverageAllowed || !overageAllowed || readOnly || isOperationsUser}
                    />
                  </div>

                  <div className='col-span-6 mb-3 sm:col-span-6'>
                    <label className='primary'>
                      Contract Received Date
                    </label>
                    <DatePicker
                      name='contract-received-date'
                      selected={moment(contractReceivedDate).toDate()}
                      onChange={(date: Date) => { setContractReceivedDate(date!); changesMade(); }}
                      dateFormat="MM-yyyy"
                      showMonthYearPicker
                      disabled={readOnly || isOperationsUser}
                    />
                  </div>

                  <div className='col-span-6 mb-4 sm:col-span-6'>
                    <label className='primary'>
                      Contract Expiration Year
                    </label>
                    <DatePicker
                      name='contract-expiration-year'
                      selected={moment(contractExpirationYear).toDate()}
                      onChange={(date: Date) => { setContractExpirationYear(date!); changesMade(); }}
                      dateFormat="yyyy"
                      showYearPicker
                      disabled={readOnly || isOperationsUser}
                    />
                  </div>

                  <div className='col-span-6 mt-2 mb-6 sm:col-span-6'>
                    <div className='flex items-start'>
                      <div className='flex items-center h-5'>
                        <input
                          id='is-multi-year-contract'
                          name='is-multi-year-contract'
                          type='checkbox'
                          className='primary-checkbox'
                          checked={isMultiYearContract}
                          onChange={(e) => { setIsMultiYearContract(e.currentTarget.checked); changesMade(); }}
                          disabled={readOnly || isOperationsUser}
                          readOnly={readOnly || isOperationsUser}
                        />
                      </div>
                      <div className='ml-3 text-sm'>
                        <label htmlFor='multi-year-contract' className='primary'>
                          Multi-Year Contract?
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Directors */}
                <div className='col-span-6 mb-10 sm:col-span-6'>
                  <div className=''>
                    <div className='grid grid-cols-4 gap-2'>
                      <h2 className="col-span-3">Directors</h2>
                      <button
                        disabled={readOnly || isOperationsUser}
                        className="col-span-1"
                        onClick={() => {
                          //Clear out the modal fields
                          let disc: DirectorDiscipline[] = [{ name: '', disciplineId: 0, districtId: districtId ?? 0 }];
                          setAddDirectorName('');
                          setAddDirectorDiscipline(disc[0]);
                          setAddDirectorDisciplines(disc);
                          setDirectorId(undefined);
                          setOpenAddDirectorModal(true);
                          setAddDirectorDisciplineGroups([]);
                        }}
                      >Add Director</button>
                    </div>
                    <div>
                      <div className=''>
                        <div>
                          {directorsData !== undefined &&
                            directorsData.length > 0 && (
                              <div>
                                <table className='min-w-full divide-y divide-gray-200'>
                                  <thead>
                                    <tr>
                                      <td>Name</td>
                                      <td>Disciplines</td>
                                      {/*<td>Discipline Groups</td>*/}
                                    </tr>
                                  </thead>
                                  <tbody className='bg-white divide-y divide-gray-200'>
                                    {directorsData.map((d) => {
                                      return (
                                        <tr
                                          key={`director_${d.directorId}`}
                                          className='cursor-pointer hover:bg-gray-100'
                                          id={`director_${d.directorId}`}
                                          onClick={(e) => {
                                            if (!readOnly && !isOperationsUser) {
                                              setDirectorId(d.directorId ?? undefined);
                                              loadDirector(d.directorId);
                                              setOpenAddDirectorModal(true);
                                              setAddDirectorDisciplineGroups([]);
                                            }
                                          }}
                                        >
                                          <td className='py-4 break-word'>
                                            <div className='text-sm text-gray-900'>
                                              {`${d?.user?.lastName ?? ''}, ${d?.user?.firstName ?? ''}`.trim() || d.name}
                                            </div>
                                          </td>
                                          <td className='py-4 break-word'>
                                            <div className='text-sm text-gray-900'>
                                              {d.directorDisciplines && d.directorDisciplines.length > 0 && <h4 className="font-semibold">Disciplines:</h4>}
                                              {d.directorDisciplines && d.directorDisciplines.length > 0 ?
                                                d.directorDisciplines.map((dd, i) => (
                                                  <div key={`directorDiscipline${i}`}>
                                                    <h4>{dd.discipline?.name ?? dd.name}</h4>
                                                  </div>
                                                ))
                                                :
                                                <></>
                                              }

                                              {d.disciplineGroups && d.disciplineGroups.length > 0 && <h4 className="mt-2 font-semibold">Discipline Groups:</h4>}
                                              {d.disciplineGroups && d.disciplineGroups.length > 0 ?
                                                d.disciplineGroups.map((ddg, i) => (
                                                  <div key={`directorDisciplineGroup${i}`}>
                                                    <h4>{ddg.name ?? ''}</h4>
                                                  </div>
                                                ))
                                                :
                                                <></>
                                              }
                                            </div>
                                          </td>
                                          {/*<td className='py-4 break-word'>
                                            <div className='text-sm text-gray-900'>
                                              {d.disciplineGroups && d.disciplineGroups.length > 0 ?
                                                d.disciplineGroups.map((dd, i) => (
                                                  <div key={`directorDisciplineGroup${i}`}>
                                                    <h4>{dd.name ?? ''}</h4>
                                                  </div>
                                                ))
                                                :
                                                <>{`None`}</>
                                              }
                                            </div>
                                          </td>*/}
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contacts */}
                <div className='col-span-6 sm:col-span-6'>
                  <div className=''>
                    <div className='grid grid-cols-4 gap-2 mb-5'>
                      <h2 className='col-span-3'>Contacts</h2>
                      <button
                        disabled={readOnly || isOperationsUser}
                        className='col-span-1'
                        onClick={() => {
                          setAddContactId(0);
                          setAddContactName('');
                          setAddContactLastName('');
                          setAddContactIsInvoice(false);
                          setAddContactTitle('');
                          setAddContactEmail('');
                          setAddContactPhone(districtData.data?.data.district.phone ?? '');
                          setAddContactFax('');
                          setAddContactAddress1(districtData.data?.data.district.address1 ?? '');
                          setAddContactAddress2(districtData.data?.data.district.address2 ?? '');
                          setAddContactCity(districtData.data?.data.district.city ?? '');
                          setAddContactStateId(Number(districtData.data?.data.district.state.stateId ?? 0).toString());
                          setAddContactZipcode(districtData.data?.data.district.zipcode ?? '');
                          setAddContactNotes('');
                          setOpenAddContactModal(true);
                        }}
                      >Add Contact</button>
                    </div>
                    <div>
                      {true && contactsData !== undefined &&
                        contactsData.length > 0 && (
                          <div className="px-2">
                            <div className="flex flex-wrap -mx-2">
                              {contactsData.sort((a, b) => a.title.localeCompare(b.title))
                                .map((d) => (
                                  <div
                                    key={`contact_${d.contactId}`}
                                    id={`contact_${d.contactId}`}
                                    data-contactid={d.contactId}
                                    className="flex flex-wrap w-full p-4 px-2 mx-2 mb-4 bg-white border-t border-b border-l border-r border-gray-400 rounded contact-card "
                                  >
                                    <div className="w-1/12 ">
                                      <UserCircleIcon className='w-10 mt-1 mb-4 ml-auto mr-1 text-gray-800'></UserCircleIcon>
                                      <PencilIcon
                                        className="w-4 ml-auto mr-auto cursor-pointer"
                                        key={`contact_${d.contactId}`}
                                        onClick={(e) => {
                                          if (!readOnly && !isOperationsUser) initEditContactModal(Number(d.contactId));
                                        }}
                                        id={`contact_${d.contactId}`}
                                        data-contactid={d.contactId}
                                      ></PencilIcon>
                                    </div>
                                    <div className="w-3/12 mr-2">
                                      <div className="mb-2 ">

                                        <div className="text-lg font-bold text-gray-900">{d.lastName}, {d.name}</div>
                                        <div className="text-sm text-gray-700">{d.title}</div>
                                        {(d.isInvoice && (
                                          <div className="flex">
                                            <CheckIcon className='w-4 mr-1 text-green-400'></CheckIcon>
                                            <div className="flex-1 text-xs text-green-400">Invoice Contact</div>
                                          </div>
                                        ))}
                                        <div className="items-center mt-2">
                                          <div className="text-xs">
                                            <label className="text-xs primary">E-Mail: </label>
                                            <span className="text-gray-600">{d.email}</span>
                                          </div>
                                          <div className="text-xs">
                                            <label className="text-xs primary">Phone: </label>
                                            <span className="text-gray-600">{d.phone}</span>
                                          </div>
                                          <div className="text-xs">
                                            <label className="text-xs primary">Fax: </label>
                                            <span className="text-gray-600">{d.fax?.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</span>
                                          </div>

                                        </div>
                                      </div>
                                    </div>

                                    <div className="w-4/12 pl-2 border-l ">
                                      <div className="items-center">
                                        <div className="text-xs">
                                          <label className="text-xs primary">Address</label>
                                          <p className="mt-2 text-gray-600">{`${d.address1} ${(d.address2 !== '' ? d.address2 + ', ' : '')}${d.city}, ${(statesData.data?.data.states.filter((s) => s.stateId === d.stateId)[0]?.name ?? '')}. ${d.zipcode}`}</p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="w-3/12 pl-2 border-l ">
                                      <div className="items-center">
                                        <div className="text-xs">
                                          <label className="text-xs primary">Notes</label>
                                          <p className="mt-2 text-gray-600">{d.notes}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      {false && contactsData !== undefined &&
                        contactsData.length > 0 && (
                          <div className="px-2">
                            <div className="flex flex-wrap -mx-2">
                              {contactsData.sort((a, b) => a.title.localeCompare(b.title))
                                .map((d) => (
                                  <div
                                    key={`contact_${d.contactId}`}
                                    id={`contact_${d.contactId}`}
                                    data-contactid={d.contactId}
                                    className="flex flex-wrap w-1/4 p-4 px-2 mx-2 bg-white border-t border-b border-l border-r border-gray-400 rounded contact-card "
                                  >
                                    <div className="w-1/5 ">
                                      <UserCircleIcon className='w-10 mt-1 mr-1 text-gray-800'></UserCircleIcon>
                                    </div>
                                    <div className="w-4/5 ">
                                      <div className="relative mb-2">
                                        <PencilIcon
                                          className="absolute top-0 right-0 w-4 cursor-pointer"
                                          key={`contact_${d.contactId}`}
                                          onClick={(e) => {
                                            if (!isOperationsUser) initEditContactModal(Number(d.contactId));
                                          }}
                                          id={`contact_${d.contactId}`}
                                          data-contactid={d.contactId}
                                        ></PencilIcon>
                                        <div className="text-lg font-bold text-gray-900">{d.lastName}, {d.name}</div>
                                        <div className="text-sm text-gray-700">{d.title}</div>
                                        {(d.isInvoice && (
                                          <div className="flex">
                                            <CheckIcon className='w-4 mr-1 text-green-400'></CheckIcon>
                                            <div className="flex-1 text-xs text-green-400">Invoice Contact</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="justify-between w-full pt-2 leading-normal border-t">
                                      <Tab.Group>
                                        {/* Tab List */}
                                        <Tab.List className='tabs'>
                                          <Tab as={Fragment}>
                                            {(btn) => (
                                              <button className={btn.selected ? 'active' : ''}>
                                                Contact Info
                                              </button>
                                            )}
                                          </Tab>
                                          <Tab as={Fragment}>
                                            {(btn) => (
                                              <button className={btn.selected ? 'active' : ''}>
                                                Notes
                                              </button>
                                            )}
                                          </Tab>
                                        </Tab.List>
                                        <Tab.Panels className='tabpanels'>
                                          <Tab.Panel>
                                            <div className="items-center">
                                              <div className="text-xs">
                                                <label className="text-xs primary">E-Mail: </label>
                                                <span className="text-gray-600">{d.email}</span>
                                              </div>
                                              <div className="text-xs">
                                                <label className="text-xs primary">Phone: </label>
                                                <span className="text-gray-600">{d.phone}</span>
                                              </div>
                                              <div className="text-xs">
                                                <label className="text-xs primary">Fax: </label>
                                                <span className="text-gray-600">{d.fax?.replace(/\D+/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</span>
                                              </div>
                                              <hr className="mt-2 mb-1" />
                                              <div className="text-xs">
                                                <label className="text-xs primary">Address</label>
                                                <p className="text-gray-600">{`${d.address1} ${(d.address2 !== '' ? d.address2 + ', ' : '')}${d.city}, ${(statesData.data?.data.states.filter((s) => s.stateId === d.stateId)[0]?.name ?? '')}. ${d.zipcode}`}</p>
                                              </div>
                                            </div>
                                          </Tab.Panel>
                                          <Tab.Panel>
                                            <div className="items-center">
                                              <div className="text-xs">
                                                <label className="text-xs primary">Notes</label>
                                                <p className="text-gray-600">{d.notes}</p>
                                              </div>
                                            </div>
                                          </Tab.Panel>
                                        </Tab.Panels>
                                      </Tab.Group>
                                    </div>
                                  </div>

                                ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </Tab.Panel>

              {/* Schools */}
              <Tab.Panel>
                <div className='col-span-6 sm:col-span-6'>
                  <div className='flex'>
                    <h2 className='flex-1 mt-1'>Schools</h2>
                    <button
                      disabled={readOnly || isOperationsUser}
                      type='button'
                      className='flex-3'
                      onClick={() =>
                        history.push(`/admin/districts/${districtId}/schools/new`)
                      }
                    >
                      Add School
                    </button>
                  </div>
                </div>
                <div className='col-span-6 mt-4 sm:col-span-6'>
                  {districtData.data?.data.district !== null && districtData.data?.data.district !== undefined && (
                    <>
                      {districtData.data?.data.district.schools !== undefined &&
                        districtData.data?.data.district.schools.length > 0 && (
                          <div>
                            <table className='min-w-full divide-y divide-gray-200'>
                              <tbody className='bg-white divide-y divide-gray-200'>
                                {districtData.data?.data.district.schools
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map((d) => (
                                    <tr
                                      key={`school_${d.schoolId}`}
                                      className='cursor-pointer hover:bg-gray-100'
                                      onClick={() => {
                                        if (!readOnly && !isOperationsUser) {
                                          history.push(`/admin/districts/${districtId}/schools/${d.schoolId}`)
                                        }
                                      }
                                      }
                                    >
                                      <td className='px-6 py-4 break-word'>
                                        <div className='text-sm text-gray-900'>
                                          {d.name}
                                        </div>
                                      </td>

                                      <td className='flex justify-end px-6 py-4 text-sm font-medium'>
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          className='w-5 h-5 text-gray-900'
                                          viewBox='0 0 20 20'
                                          fill='currentColor'
                                        >
                                          <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                                        </svg>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </Tab.Panel>

              {/* Programs */}
              <Tab.Panel>
                <div className='col-span-6 sm:col-span-6'>
                  <div className='flex'>
                    <h2 className='flex-1 mt-2'>Programs</h2>
                    <button
                      type='button'
                      disabled={readOnly || isOperationsUser}
                      className='flex-3'
                      onClick={() => history.push(`/admin/districts/${districtId}/programs/new`, { message: '' })}
                    >
                      Add Program
                    </button>
                  </div>
                </div>
                <div className='col-span-6 mt-4 sm:col-span-6'>
                  {districtData.data?.data.district !== null && districtData.data?.data.district !== undefined && (
                    <>
                      {districtData.data?.data.district.programs !== undefined &&
                        districtData.data?.data.district.programs.length > 0 && (
                          <div>
                            <table className='min-w-full divide-y divide-gray-200'>
                              <tbody className='bg-white divide-y divide-gray-200'>
                                {districtData.data?.data.district.programs
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map((d) => (
                                    <tr
                                      key={`school_${d.programId}`}
                                      className='cursor-pointer hover:bg-gray-100'
                                      onClick={() => {
                                        if (!readOnly && !isOperationsUser) {
                                          history.push(`/admin/districts/${districtId}/programs/${d.programId}`)
                                        }
                                      }
                                      }
                                    >
                                      <td className='px-6 py-4 break-word'>
                                        <div className='text-sm text-gray-900'>
                                          {d.name}
                                        </div>
                                      </td>

                                      <td className='flex justify-end px-6 py-4 text-sm font-medium'>
                                        <svg
                                          xmlns='http://www.w3.org/2000/svg'
                                          className='w-5 h-5 text-gray-900'
                                          viewBox='0 0 20 20'
                                          fill='currentColor'
                                        >
                                          <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                                        </svg>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                    </>
                  )}
                </div>
              </Tab.Panel>

              {/* Therapy Team */}
              <Tab.Panel>
                <h2>Therapy Team</h2>
                <div className='table-200'>
                  <div>
                    {/* Current Therapy Team */}
                    {hasValue(therapyTeam) && hasValue(therapyTeam?.current) && (
                      <div>
                        <h2>Current</h2>
                        <table className='min-w-full divide-y divide-gray-200 therapy-team-table'>
                          <thead>
                            <tr>
                              <td>Therapist</td>
                              <td>Discipline</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                              <td>Active</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {therapyTeam?.current.map((t: TherapyTeamUser, i) => (
                              <tr
                                key={`therapist_${t.user.userId}`}
                                className='cursor-pointer hover:bg-gray-100'
                              >
                                {/*<td>
                                <div className='py-2 text-sm text-gray-900'>
                                  {t?.therapists?.map((t: any, index: number) => <Fragment key={`ts-${i}-${index}`}>{t.school}</Fragment>)}
                                </div>
                              </td>*/}
                                <td>
                                  <div className='w-20 py-2 text-sm text-gray-900'>
                                    {t?.user.firstName === null && t.user.lastName === null ? 'Not Found' : t?.user.firstName + ' ' + t?.user.lastName}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    <ul>
                                      {t.disciplines.map((discipline, di) => (<li className={`therapy-team-discipline-${discipline.replace(' ', '-')}-current-${di}`} key={`therapy-team-discipline-${discipline.replace(' ', '-')}-current-${di}`}>{discipline}</li>))}
                                    </ul>
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {moment(t.startDate).format('l')}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {moment(t.endDate).format('l')}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {!t.user.deletedOn ? 'Y' : 'N'}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Past Therapy Team */}
                    {hasValue(therapyTeam) && hasValue(therapyTeam?.past) && (
                      <div className='py-4 mt-4 border-t'>
                        <h2>Past</h2>
                        <table className='min-w-full divide-y divide-gray-200 therapy-team-table'>
                          <thead>
                            <tr>
                              <td>Therapist</td>
                              <td>Discipline</td>
                              <td>Start Date</td>
                              <td>End Date</td>
                              <td>Active</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {therapyTeam?.past.map((t: TherapyTeamUser, i) => (
                              <tr
                                key={`therapist_${t.user.userId}`}
                                className='cursor-pointer hover:bg-gray-100'
                              >
                                {/*<td>
                                <div className='py-2 text-sm text-gray-900'>
                                  {t?.therapists?.map((t: any, index: number) => <Fragment key={`ts-${i}-${index}`}>{t.school}</Fragment>)}
                                </div>
                              </td>*/}
                                <td>
                                  <div className='w-20 py-2 text-sm text-gray-900'>
                                    {t?.user.firstName === null && t.user.lastName === null ? 'Not Found' : t?.user.firstName + ' ' + t?.user.lastName}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    <ul>
                                      {t.disciplines.map((discipline, di) => (<li className={`therapy-team-discipline-${discipline.replace(' ', '-')}-past-${di}`} key={`therapy-team-discipline-${discipline.replace(' ', '-')}-past-${di}`}>{discipline}</li>))}
                                    </ul>
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {moment(t.startDate).format('l')}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {moment(t.endDate).format('l')}
                                  </div>
                                </td>
                                <td>
                                  <div className='py-2 text-sm text-gray-900'>
                                    {!t.user.deletedOn ? 'Y' : 'N'}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </Tab.Panel>

              {/* Calendar */}
              <Tab.Panel>
                <h2>Calendar</h2>
                <FullCalendar
                  editable={true}
                  selectable={true}
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  select={(data) => {

                    initEditCalendarItemModal(data.start, data.end);

                  }}
                  events={calendarEvents}
                  displayEventTime={false}
                  eventClick={(e) => {

                    setAddCalendarItemId(Number(e.event.id));
                    setAddCalendarItemIsVisible(true);
                    setCalendarNotes(e.event.extendedProps.notes === '' ? 'No notes ' : e.event.extendedProps.notes);
                    setOpenCalendarNotesModal(true);

                  }}
                  eventContent={renderEventContent}
                  eventStartEditable={false}
                  expandRows={true}
                  longPressDelay={10}
                />
              </Tab.Panel>

              {/* Billing Rates */}
              <Tab.Panel>
                <div className="table-200">
                  <div className="grid grid-cols-4 gap-2 mb-5">
                    <h2 className='col-span-3'>Rate History</h2>
                    <button
                      className='col-span-1'
                      disabled={readOnly}
                      onClick={() => history.push(`/admin/districts/${districtId}/rates/new`, { message: '' })}
                    >Add Rate</button>
                  </div>

                  {/* Pending Rates */}
                  <h2>Pending Approval Rates</h2>
                  <div>
                    {billingRatesPending !== undefined &&
                      billingRatesPending.length > 0 ? (
                      <table className='min-w-full divide-y divide-gray-200'>
                        <thead>
                          <tr>
                            <td>Discipline</td>
                            <td>Program</td>
                            <td>Bill Rate</td>
                            <td>Start Date</td>
                            <td>End Date</td>
                          </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                          {billingRatesPending
                            .map((br) => (
                              <tr
                                key={`billingRateScheduled_${br.billingRateId}`}
                                className={`cursor-pointer hover:bg-gray-100 billing-rate-${br.billingRateId}`}
                                onClick={() => {
                                  if (!readOnly && !isOperationsUser) {
                                    history.push(`/admin/districts/${districtId}/rates/${br.billingRateId}`, {
                                      message: ''
                                    });
                                  }
                                }}
                              >
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {disciplines?.find(d => d.disciplineId === br.disciplineId)?.name}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {programs?.find(p => p.programId === br.programId)?.name ?? ""}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.rate.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    })}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.startDate === null ? '' : moment(new Date(br.startDate)).format('l')}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.endDate === null ? '' : moment(new Date(br.endDate)).format('l')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <h4 className='mb-5'>No Pending Rates</h4>
                    )}
                  </div>

                  {/* Scheduled Rates */}
                  <h2>Scheduled Rates</h2>
                  <div>
                    {billingRatesScheduled !== undefined &&
                      billingRatesScheduled.length > 0 ? (
                      <table className='min-w-full divide-y divide-gray-200'>
                        <thead>
                          <tr>
                            <td>Discipline</td>
                            <td>Program</td>
                            <td>Bill Rate</td>
                            <td>Start Date</td>
                            <td>End Date</td>
                          </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                          {billingRatesScheduled
                            .map((br) => (
                              <tr
                                key={`billingRateScheduled_${br.billingRateId}`}
                                className='cursor-pointer hover:bg-gray-100'
                                onClick={() => {
                                  history.push(`/admin/districts/${districtId}/rates/${br.billingRateId}`, {
                                    message: ''
                                  });
                                }}
                              >
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {disciplines?.find(d => d.disciplineId === br.disciplineId)?.name}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {programs?.find(p => p.programId === br.programId)?.name ?? ""}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.rate.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    })}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.startDate === null ? '' : moment(new Date(br.startDate)).format('l')}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.endDate === null ? '' : moment(new Date(br.endDate)).format('l')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <h4 className='mb-5'>No Scheduled Rates</h4>
                    )}
                  </div>

                  {/* Active Rates */}
                  <h2>Active Rates</h2>
                  <div>
                    {billingRatesActive !== undefined &&
                      billingRatesActive.length > 0 ? (
                      <table className='min-w-full divide-y divide-gray-200'>
                        <thead>
                          <tr>
                            <td>Discipline</td>
                            <td>Program</td>
                            <td>Bill Rate</td>
                            <td>Start Date</td>
                            <td>End Date</td>
                          </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                          {billingRatesActive
                            .map((br) => (
                              <tr
                                key={`billingRateActive_${br.billingRateId}`}
                                className='cursor-pointer hover:bg-gray-100'
                                onClick={() => {
                                  if (!isOperationsUser) {
                                    history.push(`/admin/districts/${districtId}/rates/${br.billingRateId}`, {
                                      message: ''
                                    });
                                  }
                                }}
                              >
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {disciplines?.find(d => d.disciplineId === br.disciplineId)?.name}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {programs?.find(p => p.programId === br.programId)?.name ?? ""}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.rate.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    })}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.startDate === null ? '' : moment(new Date(br.startDate)).format('l')}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.endDate === null ? '' : moment(new Date(br.endDate)).format('l')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <h4 className='mb-5'>No Active Rates</h4>
                    )}
                  </div>

                  {/* Past Rates */}
                  <h2>Past Rates</h2>
                  <div>
                    {billingRatesPast !== undefined &&
                      billingRatesPast.length > 0 ? (
                      <table className='min-w-full divide-y divide-gray-200'>
                        <thead>
                          <tr>
                            <td>Discipline</td>
                            <td>Program</td>
                            <td>Bill Rate</td>
                            <td>Start Date</td>
                            <td>End Date</td>
                          </tr>
                        </thead>
                        <tbody className='bg-white divide-y divide-gray-200'>
                          {billingRatesPast
                            .map((br) => (
                              <tr
                                id={`billingRatePast_${br.billingRateId}`}
                                key={`billingRatePast_${br.billingRateId}`}
                                className='cursor-pointer hover:bg-gray-100'
                                onClick={() => {
                                  if (!isOperationsUser) {
                                    history.push(`/admin/districts/${districtId}/rates/${br.billingRateId}`, {
                                      message: ''
                                    });
                                  }
                                }}
                              >
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {disciplines?.find(d => d.disciplineId === br.disciplineId)?.name}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {programs?.find(p => p.programId === br.programId)?.name ?? ""}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.rate.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                    })}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.startDate === null ? '' : moment(new Date(br.startDate)).format('l')}
                                  </div>
                                </td>
                                <td className='py-4 break-word'>
                                  <div className='text-sm text-gray-900'>
                                    {br.endDate === null ? '' : moment(new Date(br.endDate)).format('l')}
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <h4 className='mb-5'>No Past Rates</h4>
                    )}
                  </div>
                </div>
              </Tab.Panel>

              {/* AR/Invoice */}
              <Tab.Panel>
                <h2>Billing Contacts</h2>
                <div className='mt-2'>
                  {contactsData !== undefined &&
                    contactsData.length > 0 && (
                      <div>
                        <table className='min-w-full divide-y divide-gray-200'>
                          <thead>
                            <tr>
                              <td>Name</td>
                              <td>Title</td>
                              <td>Email</td>
                              <td>Phone</td>
                              <td>Address</td>
                              <td>Notes</td>
                            </tr>
                          </thead>
                          <tbody className='bg-white divide-y divide-gray-200'>
                            {contactsData
                              .sort((a, b) => a.title.localeCompare(b.title))
                              .filter((c) => c.isInvoice)
                              .map((t) => (
                                <tr
                                  key={`billingContact_${t.contactId}`}
                                >
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {t.lastName}, {t.name}
                                    </div>
                                  </td>
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {t.title}
                                    </div>
                                  </td>
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {t.email}
                                    </div>
                                  </td>
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {formatPhoneNumber(t.phone ?? phone)}
                                    </div>
                                  </td>
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {`${t.address1}, ${t.address2 ? `${t.address2}, ` : ''}${t.city}, ${(statesData.data?.data.states.find((s) => s.stateId === t.stateId)?.name ?? '')}. ${t.zipcode}`}
                                    </div>
                                  </td>
                                  <td className='py-4 break-word'>
                                    <div className='text-sm text-gray-900'>
                                      {t.notes ?? 'None'}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                </div>
                <h2 className='mt-2'>Invoice Documents</h2>
                <div className='flex'>
                  <div className='flex-1'>
                    <input
                      id='invoice-summary'
                      name='invoice-summary'
                      type='checkbox'
                      className='primary-checkbox'
                      checked={isInvoiceSummary}
                      onChange={(e) => { setIsInvoiceSummary(e.currentTarget.checked); changesMade(); }}
                      disabled={readOnly || isOperationsUser}
                      readOnly={readOnly || isOperationsUser}
                    />
                    <label htmlFor='invoice-summary' className='px-2 py-2 primary'>Invoice Summary</label>
                  </div>
                  <div className='flex-1'>
                    <input
                      id='invoice-detail'
                      name='invoice-detail'
                      type='checkbox'
                      className='primary-checkbox'
                      checked={isInvoiceDetail}
                      onChange={(e) => { setIsInvoiceDetail(e.currentTarget.checked); changesMade(); }}
                      disabled={readOnly || isOperationsUser}
                      readOnly={readOnly || isOperationsUser}
                    />
                    <label htmlFor='invoice-detail' className='px-2 py-2 primary'>Invoice Detail</label>
                  </div>
                  <div className='flex-1'>
                    <input
                      id='cpc-report'
                      name='cpc-report'
                      type='checkbox'
                      className='primary-checkbox'
                      checked={isCdcReport}
                      onChange={(e) => { setIsCdcReport(e.currentTarget.checked); changesMade(); }}
                      disabled={readOnly || isOperationsUser}
                      readOnly={readOnly || isOperationsUser}
                    />
                    <label htmlFor='cpc-report' className='px-2 py-2 primary'>CPC Report</label>
                  </div>
                </div>
                <div className='mt-2'>
                  <label className='mb-2'>Delivery Method:</label>
                  <select
                    className='w-full'
                    onChange={(e) => { setDeliveryMethod(e.target.value); changesMade(); }}
                    value={deliveryMethod}
                    disabled={readOnly || isOperationsUser}
                  >
                    <option key="deliveryMethod_Email">Email</option>
                    <option key="deliveryMethod_Fax">Fax</option>
                    <option key="deliveryMethod_Mail">Mail</option>
                  </select>
                </div>
                <div className='mt-2'>
                  <label className='mb-2'>Payment By:</label>
                  <select
                    className='w-full'
                    onChange={(e) => { setPaymentBy(e.target.value); changesMade(); }}
                    value={paymentBy}
                    disabled={readOnly || isOperationsUser}
                  >
                    <option key="paymentBy_ACH">ACH</option>
                    <option key="paymentBy_Check">Check</option>
                  </select>
                </div>
                <div className='mt-2'>
                  <div><label className='mb-2'>Invoice Due Date:</label></div>
                  <DatePicker
                    className='block mt-1 primary'
                    selected={moment(invoiceDueDate).toDate()}
                    minDate={new Date()}
                    name='invoiceDueDate'
                    onChange={(date: Date) => { setInvoiceDueDate(date!); changesMade(); }}
                    dateFormat="MM/dd/yyyy"
                    disabled={readOnly || isOperationsUser}
                  />
                </div>
                <h2 className="mt-4">Invoice Report Grouping</h2>
                <div>
                  <label className='mb-2'>Group Invoice Summary By:</label>
                  <select
                    className='w-full'
                    onChange={(e) => { setGroupInvoiceSummaryBy(e.target.value); changesMade(); }}
                    value={groupInvoiceSummaryBy}
                    disabled={readOnly || isOperationsUser}
                  >
                    <option key="groupInvoiceSummaryBy_Discipline">Discipline</option>
                    <option key="groupInvoiceSummaryBy_Grade">Grade</option>
                    <option key="groupInvoiceSummaryBy_School">School</option>
                  </select>
                </div>
                <div className='mt-2'>
                  <label className='mb-2'>Group Invoice Detail By:</label>
                  <select
                    className='w-full'
                    onChange={(e) => { setGroupInvoiceDetailBy(e.target.value); changesMade(); }}
                    value={groupInvoiceDetailBy}
                    disabled={readOnly || isOperationsUser}
                  >
                    <option key="groupInvoiceDetailBy_Discipline">Discipline</option>
                    <option key="groupInvoiceDetailBy_Grade">Grade</option>
                    <option key="groupInvoiceDetailBy_School">School</option>
                  </select>
                </div>
                <div className='mt-2'>
                  <label className='mb-2'>Additional Invoice Information:</label>
                  <textarea
                    value={invoicingNotes}
                    onChange={(e) => { setInvoicingNotes(e.target.value); changesMade(); }}
                    disabled={readOnly || isOperationsUser}
                    readOnly={readOnly || isOperationsUser}
                  ></textarea>
                </div>
              </Tab.Panel>

              {/* ESY Tab */}
              <Tab.Panel>
                <h2>ESY</h2>
                <div className='grid grid-cols-4 gap-2 mb-6'>
                  <div className='col-span-2 sm:col-span-2'>
                    <label className='primary'>ESY Start Date {esyEndDate && !esyStartDate && (
                      <span className='pl-1 text-red-500'>*</span>
                    )}</label>
                    <DatePicker
                      name='esy-start-date'
                      minDate={new Date()}
                      maxDate={esyEndDate}
                      selected={esyStartDate ? esyStartDate && moment(esyStartDate).isValid() ? moment(esyStartDate).toDate() : new Date() : null}
                      onChange={(date: Date) => setEsyStartDate(date!)}
                      dateFormat="MM/dd/yyyy"
                      disabled={readOnly}
                    />
                  </div>
                  <div className='col-span-2 sm:col-span-2'>
                    <label className='primary'>ESY End Date{esyStartDate && !esyEndDate && (
                      <span className='pl-1 text-red-500'>*</span>
                    )}</label>
                    <DatePicker
                      name='esy-end-date'
                      minDate={esyStartDate ? esyStartDate : new Date()}
                      selected={esyEndDate ? esyEndDate && moment(esyEndDate).isValid() ? moment(esyEndDate).toDate() : new Date() : null}
                      onChange={(date: Date) => setEsyEndDate(date!)}
                      dateFormat="MM/dd/yyyy"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </Tab.Panel>

              {/* Metrics/Rules */}
              {/*<Tab.Panel>
                <h2>Metrics/Rules</h2>
              </Tab.Panel>*/}
            </Tab.Panels>
          </Tab.Group>

          <hr className='py-4' />
          <div className=''>
            {/*<SubmitButton
              className='secondary'
              text='Remove'
              spinning={remove.isPending}
              onClick={handleRemove}
            />*/}
            <SubmitButton
              text='Save District'
              disabled={readOnly || name.length === 0 || selectedDistrictBillingSpecialistsOptions.length === 0 || isOperationsUser || isESYDateMismatch()}
              spinning={save.isPending}
              onClick={() => save.run()}
              className='float-right'
            />
          </div>
        </Fragment>
      </Layout >
    </Fragment >
  );
};

export default EditDistrict;